import {
  doc,
  getDoc,
  addDoc,
  setDoc,
  collection,
  serverTimestamp,
  query,
  where,
  getDocs,
  updateDoc,
  increment,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";

const LISTINGS_COLLECTION = "listings";
const INQUIRIES_COLLECTION = "inquiries";

/**
 * ListingModel - Handles public vacancy listings and lead inquiries
 *
 * This model is designed for public-facing rental listings that can be
 * shared on social media, Zillow, etc. without requiring authentication.
 */
const listingModel = {
  /**
   * Fetch a public listing by ID (alias for backwards compatibility)
   * This should work without authentication for public sharing
   *
   * @param {string} listingId - The listing document ID
   * @returns {Promise<Object|null>} Listing data or null if not found
   */
  getPublicListingById: async (listingId) => {
    try {
      const docRef = doc(db, LISTINGS_COLLECTION, listingId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();

      // Only return if listing is active/published
      if (data.status !== "active" && data.status !== "published") {
        return null;
      }

      return {
        id: docSnap.id,
        ...data,
        // Convert Firestore timestamps to JS dates for easier handling
        createdAt: data.createdAt?.toDate?.() || null,
        updatedAt: data.updatedAt?.toDate?.() || null,
        availableDate: data.availableDate?.toDate?.() || null,
      };
    } catch (error) {
      console.error("Error fetching public listing:", error);
      throw error;
    }
  },

  /**
   * Fetch a public listing by ID
   * This should work without authentication for public sharing
   *
   * @param {string} listingId - The listing document ID
   * @returns {Promise<Object|null>} Listing data or null if not found
   */
  getPublicListing: async (listingId) => {
    try {
      const docRef = doc(db, LISTINGS_COLLECTION, listingId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();

      // Only return if listing is active/published
      if (data.status !== "active" && data.status !== "published") {
        return null;
      }

      return {
        id: docSnap.id,
        ...data,
        // Convert Firestore timestamps to JS dates for easier handling
        createdAt: data.createdAt?.toDate?.() || null,
        updatedAt: data.updatedAt?.toDate?.() || null,
        availableDate: data.availableDate?.toDate?.() || null,
      };
    } catch (error) {
      console.error("Error fetching public listing:", error);
      throw error;
    }
  },

  /**
   * Create a new inquiry/lead for a listing
   *
   * @param {Object} inquiryData - The inquiry data
   * @param {string} inquiryData.listingId - ID of the listing
   * @param {string} inquiryData.name - Prospect's name
   * @param {string} inquiryData.email - Prospect's email
   * @param {string} inquiryData.phone - Prospect's phone
   * @param {Date} inquiryData.moveInDate - Desired move-in date
   * @param {boolean} inquiryData.hasPets - Whether they have pets
   * @param {string} inquiryData.creditScore - Credit score range
   * @param {number} inquiryData.monthlyIncome - Combined monthly income
   * @param {string} inquiryData.message - Optional message
   * @returns {Promise<Object>} Created inquiry with ID
   */
  createInquiry: async (inquiryData) => {
    try {
      const inquiryRef = await addDoc(collection(db, INQUIRIES_COLLECTION), {
        ...inquiryData,
        status: "new",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Track if they're qualified based on requirements
        isQualified: inquiryData.isQualified || null,
        // Track source for analytics
        source: inquiryData.source || "vacancy_page",
      });

      // Increment view/inquiry counter on the listing
      const listingRef = doc(db, LISTINGS_COLLECTION, inquiryData.listingId);
      await updateDoc(listingRef, {
        inquiryCount: increment(1),
        lastInquiryAt: serverTimestamp(),
      });

      return {
        id: inquiryRef.id,
        ...inquiryData,
      };
    } catch (error) {
      console.error("Error creating inquiry:", error);
      throw error;
    }
  },

  /**
   * Increment view count for a listing (fire and forget)
   *
   * @param {string} listingId - The listing ID
   */
  incrementViewCount: async (listingId) => {
    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
      await updateDoc(listingRef, {
        viewCount: increment(1),
        lastViewedAt: serverTimestamp(),
      });
    } catch (error) {
      // Silent fail - we don't want to break the page if analytics fail
      console.warn("Failed to increment view count:", error);
    }
  },

  /**
   * Get inquiries for a listing (landlord dashboard use)
   *
   * @param {string} listingId - The listing ID
   * @returns {Promise<Array>} Array of inquiries
   */
  getInquiriesForListing: async (listingId) => {
    try {
      const q = query(
        collection(db, INQUIRIES_COLLECTION),
        where("listingId", "==", listingId)
      );

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || null,
        updatedAt: doc.data().updatedAt?.toDate?.() || null,
      }));
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      throw error;
    }
  },

  /**
   * Update inquiry status (e.g., contacted, scheduled, rejected)
   *
   * @param {string} inquiryId - The inquiry ID
   * @param {string} status - New status
   * @returns {Promise<void>}
   */
  updateInquiryStatus: async (inquiryId, status) => {
    try {
      const inquiryRef = doc(db, INQUIRIES_COLLECTION, inquiryId);
      await updateDoc(inquiryRef, {
        status,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating inquiry status:", error);
      throw error;
    }
  },

  // ============================================
  // LANDLORD DASHBOARD METHODS
  // ============================================

  /**
   * Get all listings for a landlord
   *
   * @param {string} landlordId - The landlord's user ID
   * @returns {Promise<Array>} Array of listing documents
   */
  getListingsByLandlord: async (landlordId) => {
    try {
      const q = query(
        collection(db, LISTINGS_COLLECTION),
        where("landlordId", "==", landlordId)
      );

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || null,
        updatedAt: doc.data().updatedAt?.toDate?.() || null,
        availableDate: doc.data().availableDate?.toDate?.() || null,
      }));
    } catch (error) {
      console.error("Error fetching landlord listings:", error);
      throw error;
    }
  },

  /**
   * Create a new listing
   * Enforces ONE listing per unit constraint
   *
   * @param {Object} listingData - The listing data
   * @returns {Promise<Object>} Created listing with ID
   * @throws {Error} If unit already has a listing
   */
  createListing: async (listingData, listingId = null) => {
    try {
      // Check if unit already has a listing (one-per-unit constraint)
      const existingListingsQuery = query(
        collection(db, LISTINGS_COLLECTION),
        where("unitId", "==", listingData.unitId)
      );

      const existingListings = await getDocs(existingListingsQuery);

      if (!existingListings.empty) {
        throw new Error(
          "This property already has an active listing. Please delete the existing listing first."
        );
      }

      // Create the listing
      const createPayload = {
        ...listingData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        viewCount: 0,
        inquiryCount: 0,
      };

      let listingRef;
      if (listingId) {
        listingRef = doc(db, LISTINGS_COLLECTION, listingId);
        await setDoc(listingRef, createPayload);
      } else {
        listingRef = await addDoc(
          collection(db, LISTINGS_COLLECTION),
          createPayload
        );
      }

      const newListing = await getDoc(listingRef);

      return {
        id: newListing.id,
        ...newListing.data(),
      };
    } catch (error) {
      console.error("Error creating listing:", error);
      throw error;
    }
  },

  /**
   * Update listing status (active/inactive)
   *
   * @param {string} listingId - The listing ID
   * @param {string} status - New status ('active' or 'inactive')
   * @returns {Promise<void>}
   */
  updateListingStatus: async (listingId, status) => {
    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
      await updateDoc(listingRef, {
        status,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating listing status:", error);
      throw error;
    }
  },

  /**
   * Delete a listing
   *
   * @param {string} listingId - The listing ID
   * @returns {Promise<void>}
   */
  deleteListing: async (listingId) => {
    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
      await deleteDoc(listingRef);
    } catch (error) {
      console.error("Error deleting listing:", error);
      throw error;
    }
  },

  /**
   * Update listing details
   *
   * @param {string} listingId - The listing ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  updateListing: async (listingId, updates) => {
    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
      await updateDoc(listingRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating listing:", error);
      throw error;
    }
  },
};

/**
 * Listing Data Structure (for reference)
 *
 * {
 *   id: string,
 *   unitId: string,              // Reference to unit in units collection
 *   landlordId: string,          // Owner ID
 *   status: 'draft' | 'active' | 'published' | 'inactive',
 *
 *   // Property Details
 *   address: string,
 *   city: string,
 *   state: string,
 *   zipCode: string,
 *   bedrooms: number,
 *   bathrooms: number,
 *   sqft: number,
 *   rentAmount: number,
 *   securityDeposit: number,
 *
 *   // Media
 *   photos: string[],            // Array of image URLs
 *   virtualTourUrl: string,      // Optional
 *
 *   // Description
 *   title: string,
 *   description: string,
 *   amenities: string[],
 *
 *   // Requirements (The Velvet Rope)
 *   minCreditScore: number,      // e.g., 650
 *   minIncomeMultiplier: number, // e.g., 3 (3x rent)
 *   petsAllowed: boolean,
 *   petDeposit: number,
 *
 *   // Availability
 *   availableDate: Date,
 *   leaseTerm: string,           // e.g., "12 months"
 *
 *   // Landlord Info (limited for privacy)
 *   landlordName: string,        // First name only
 *   landlordVerified: boolean,   // Show verified badge
 *   responseTime: string,        // "Usually responds within 24 hours"
 *
 *   // Analytics
 *   viewCount: number,
 *   inquiryCount: number,
 *   lastViewedAt: Date,
 *   lastInquiryAt: Date,
 *
 *   // Metadata
 *   createdAt: Date,
 *   updatedAt: Date,
 * }
 */

/**
 * Inquiry Data Structure (for reference)
 *
 * {
 *   id: string,
 *   listingId: string,
 *   landlordId: string,         // Denormalized for easier queries
 *
 *   // Prospect Info
 *   name: string,
 *   email: string,
 *   phone: string,
 *
 *   // Qualifying Questions (The Velvet Rope)
 *   moveInDate: Date,
 *   hasPets: boolean,
 *   creditScore: 'excellent_720' | 'good_650' | 'fair_600' | 'unknown',
 *   monthlyIncome: number,
 *
 *   // Optional
 *   message: string,
 *
 *   // Status Tracking
 *   status: 'new' | 'contacted' | 'scheduled' | 'rejected' | 'approved',
 *   isQualified: boolean,       // Auto-calculated based on requirements
 *
 *   // Analytics
 *   source: 'vacancy_page' | 'facebook' | 'zillow' | 'direct',
 *   referrer: string,
 *
 *   // Metadata
 *   createdAt: Date,
 *   updatedAt: Date,
 * }
 */

export default listingModel;
